import { useFormikContext } from 'formik';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';

import { lookupsJson } from '@ourbranch/lookups';
import { isFarmBasedPolicyType } from '@ourbranch/farm-utils';
import { FormField } from 'core/components/form';
import { useStore } from 'core/store/store.mobx';
import { Collapsible } from 'quote/components/collapsible';

/**
 * EntityDetailsFields Component
 *
 * Reusable component for displaying entity type and entity name fields for Farm policies.
 * Used across:
 * - New offer creation (uses values.policyType)
 * - Offer modification (uses store.isFarmOffer)
 * - Policy modification (uses values.policyType)
 *
 * Entity data is stored in values.entity object.
 * Fields:
 * - entity.type: Required dropdown for Farm policies
 * - entity.name: Conditional text field (shows when type !== 'Individual' and type !== 'Joint')
 */
const EntityDetailsFields = ({ showAsCollapsible, mode, title }) => {
  const { values, setFieldValue } = useFormikContext();
  const { offer: offerStore, account } = useStore();
  const policyStore = account?.policies?.policy;

  // Check if this is a Farm policy using multiple methods:
  // 1. values.policyType for new offer creation and policy modification
  // 2. offerStore?.isFarmOffer for offer modification (uses selectedOption)
  // 3. policyStore?.isFarmOffer for policy modification in sold policies
  const isFarm = isFarmBasedPolicyType(values.policyType) || offerStore?.isFarmOffer || policyStore?.isFarmOffer;
  if (!isFarm) {
    return null;
  }

  // Get entity data
  const entity = values.entity;
  const entityType = entity?.type;
  const entityName = entity?.name;
  const entityAddress = entity?.address;

  // Initialize entity object if it doesn't exist when we need to set a value
  const ensureEntityObject = () => {
    if (!values.entity) {
      setFieldValue('entity', {});
    }
  };

  // Clear name when switching to Individual or Joint to prevent stale data
  useEffect(() => {
    if ((entityType === 'Individual' || entityType === 'Joint') && entityName) {
      setFieldValue('entity.name', undefined);
    }
  }, [entityType, entityName, setFieldValue]);

  // Clear address when switching away from Trust to prevent stale data
  useEffect(() => {
    if (entityType !== 'Trust' && entityAddress) {
      setFieldValue('entity.address', undefined);
    }
  }, [entityType, entityAddress, setFieldValue]);

  // Show entity name field when entity type is not Individual or Joint
  const showEntityName = entityType && entityType !== 'Individual' && entityType !== 'Joint';

  const fields = (
    <>
      <FormField
        id="entity.type"
        name="entity.type"
        type="select"
        label="Entity Type"
        options={lookupsJson.entityTypesOptions}
        mode={mode}
        xs={6}
        required
        onFocus={ensureEntityObject}
      />

      {showEntityName && (
        <FormField
          id="entity.name"
          name="entity.name"
          type="string"
          label="Entity name or company"
          mode={mode}
          xs={6}
          required
        />
      )}
    </>
  );

  if (showAsCollapsible) {
    return (
      <Collapsible title={title} initialOpen>
        {fields}
      </Collapsible>
    );
  }

  return fields;
};

EntityDetailsFields.propTypes = {
  showAsCollapsible: PropTypes.bool,
  mode: PropTypes.oneOf(['light', 'dark']),
  title: PropTypes.string
};

EntityDetailsFields.defaultProps = {
  showAsCollapsible: true,
  mode: 'light',
  title: 'Entity Details'
};

export default observer(EntityDetailsFields);
